import {
  ArcElement,
  Chart as ChartJS,
  Legend,
  Tooltip
} from 'chart.js';
import { useEffect, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';

import '../css/Drivers.css';
import useIdleLogout from "../hooks/useIdleLogout";

ChartJS.register(ArcElement, Tooltip, Legend);

export default function Drivers() {
  useIdleLogout();
  const [drivers, setDrivers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');

  useEffect(() => {
    setDrivers([
      { id: 1, photo: '/assets/img/admin profile.jpg', name: 'Jigme Dorji', email: 'jigme@example.com', license: 'BHT-12345', vehicle: 'Toyota Prius', color: 'White', plate: 'DR-001', status: 'Active' },
      { id: 2, photo: '/assets/img/admin profile.jpg', name: 'Pema Choden', email: 'pema@example.com', license: 'BHT-67890', vehicle: 'Honda Fit', color: 'Blue', plate: 'DR-002', status: 'Inactive' },
      { id: 3, photo: '/assets/img/admin profile.jpg', name: 'Dawa', email: 'dawa@example.com', license: 'BHT-45326', vehicle: 'Honda Fit', color: 'Purple', plate: 'DR-003', status: 'Active' },
      { id: 4, photo: '/assets/img/admin profile.jpg', name: 'Keshar Bhujel', email: 'bhujel@example.com', license: 'BHT-98765', vehicle: 'Subaru XV', color: 'Red', plate: 'DR-004', status: 'Suspended' }
    ]);
  }, []);

  const filteredDrivers = drivers.filter(d =>
    d.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
    (filterStatus === 'All' || d.status === filterStatus)
  );

  const total = drivers.length;
  const activeCount = drivers.filter(d => d.status === 'Active').length;
  const inactiveCount = drivers.filter(d => d.status === 'Inactive').length;
  const suspendedCount = drivers.filter(d => d.status === 'Suspended').length;

  const statusData = {
    labels: ['Active', 'Inactive', 'Suspended'],
    datasets: [{
      data: [activeCount, inactiveCount, suspendedCount],
      backgroundColor: ['#198754', '#6c757d', '#ffc107']
    }]
  };
  const colorData = {
    labels: ['White', 'Blue', 'Purple', 'Red'],
    datasets: [{
      data: ['White', 'Blue', 'Purple', 'Red'].map(c =>
        drivers.filter(d => d.color === c).length
      ),
      backgroundColor: ['#6c757d', '#0d6efd', '#6f42c1', '#dc3545']
    }]
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: true,
    layout: { padding: { bottom: 40 } },
    plugins: {
      legend: {
        position: 'bottom',
        labels: { boxWidth: 12, padding: 8, font: { size: 11 } }
      }
    }
  };

  return (
    <>
      {/* Metrics */}
      <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-4 mb-4 drivers-metrics">
        {[
          { label: 'Total Registered Drivers', value: total },
          { label: 'Active Drivers', value: activeCount },
          { label: 'Inactive Drivers', value: inactiveCount },
          { label: 'Suspended Drivers', value: suspendedCount }
        ].map((m, i) => (
          <div className="col h-100" key={i}>
            <div className="card h-100 metrics-card">
              <div className="card-body">
                <h6 className="text-secondary mb-2">{m.label}</h6>
                <p className="display-5 fw-bold mb-0">{m.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="row gx-4 gy-4 mb-4">
        <div className="col-md-6">
          <div className="card mb-4 chart-card">
            <div className="card-header">Driver Status Distribution</div>
            <div className="card-body">
              <Doughnut data={statusData} options={doughnutOptions} />
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card mb-4 chart-card">
            <div className="card-header">Vehicle Color Breakdown</div>
            <div className="card-body">
              <Doughnut data={colorData} options={doughnutOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="card mb-4 search-filter-card">
        <div className="card-body d-flex align-items-center">
          <div className="input-group me-3 flex-grow-1">
            <span className="input-group-text"><i className="bi bi-search" /></span>
            <input
              type="text"
              className="form-control"
              placeholder="Search drivers..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="form-select"
            style={{ maxWidth: '200px' }}
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
          >
            <option value="All">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Suspended">Suspended</option>
          </select>
        </div>
      </div>

      {/* Drivers Table */}
      <div className="drivers-table-card mb-4">
        <table className="table table-borderless table-striped align-middle mb-0">
          <thead>
            <tr>
              <th>Sl.No</th><th>Photo</th><th>Name</th>
              <th>Email</th><th>License</th><th>Vehicle</th>
              <th>Color</th><th>Plate</th><th>Status</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredDrivers.map((d, i) => (
              <tr key={d.id}>
                <td>{i + 1}</td>
                <td>
                  <img
                    src={d.photo}
                    alt=""
                    className="rounded-circle"
                    width="40"
                    height="40"
                  />
                </td>
                <td>{d.name}</td><td>{d.email}</td><td>{d.license}</td>
                <td>{d.vehicle}</td><td>{d.color}</td><td>{d.plate}</td>
                <td>
                  <span className={`badge bg-${d.status === 'Active' ? 'success' :
                      d.status === 'Inactive' ? 'secondary' :
                        'warning'
                    }`}>
                    {d.status}
                  </span>
                </td>
                <td>
                  <button className="btn btn-view me-2" aria-label="View">
                    <i className="bi bi-eye"></i>
                  </button>
                  <button className="btn btn-delete" aria-label="Delete">
                    <i className="bi bi-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <footer className="dashboard-footer mt-auto">
        <small>© {new Date().getFullYear()} SuperApp Admin • All rights reserved</small>
      </footer>
    </>
  );
}
